import { Button, Typography } from "@suraasa/placebo-ui"
import { useSuraasaPlusContext } from "features/SuraasaPlus/context"
import { useEffect, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import routes from "routes"
import { BROWSER_STORAGE_KEYS } from "utils/constants"
import { getPlatformURL, getTextGradientStyles } from "utils/helpers"

import { SpnyTourEnum, useSuraasaPlusNewYear } from "../../Context"
import { tourManager } from "../../TourManager"
import GetStarted from "../GetStarted"

const PlusSvg = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      opacity="0.34"
      d="M10.2965 1.01495C10.3039 0.925 10.3116 0.831508 10.3246 0.751456C10.3391 0.661417 10.3656 0.55026 10.431 0.438393C10.5202 0.285652 10.6532 0.163171 10.8127 0.0867678C10.9296 0.0308103 11.0425 0.0135075 11.1335 0.00634064C11.2143 -2.96341e-05 11.3081 -1.4537e-05 11.3983 0H11.3984H12.6016H12.6017C12.6919 -1.4537e-05 12.7857 -2.96342e-05 12.8665 0.00634064C12.9575 0.0135075 13.0704 0.0308103 13.1873 0.0867678C13.3468 0.163171 13.4798 0.285652 13.569 0.438393C13.6344 0.55026 13.6609 0.661417 13.6755 0.751455C13.6884 0.831508 13.6961 0.924999 13.7035 1.01494L14.4088 9.59125L22.9851 10.2965C23.075 10.3039 23.1685 10.3116 23.2486 10.3246C23.3386 10.3391 23.4497 10.3656 23.5616 10.431C23.7144 10.5202 23.8368 10.6532 23.9132 10.8127C23.9692 10.9296 23.9865 11.0425 23.9937 11.1335C24 11.2143 24 11.3081 24 11.3984V12.6016C24 12.6919 24 12.7857 23.9937 12.8665C23.9865 12.9575 23.9692 13.0704 23.9132 13.1873C23.8368 13.3468 23.7144 13.4798 23.5616 13.569C23.4497 13.6344 23.3386 13.6609 23.2486 13.6755C23.1685 13.6884 23.075 13.6961 22.9851 13.7035L22.9851 13.7035L14.4088 14.4088L13.7035 22.9851C13.6961 23.075 13.6884 23.1685 13.6755 23.2485C13.6609 23.3386 13.6344 23.4497 13.569 23.5616C13.4798 23.7143 13.3468 23.8368 13.1873 23.9132C13.0704 23.9692 12.9575 23.9865 12.8665 23.9937C12.7857 24 12.6919 24 12.6016 24H11.3984C11.3081 24 11.2143 24 11.1335 23.9937C11.0425 23.9865 10.9296 23.9692 10.8127 23.9132C10.6532 23.8368 10.5202 23.7143 10.431 23.5616C10.3656 23.4497 10.3391 23.3386 10.3246 23.2485C10.3116 23.1685 10.3039 23.075 10.2965 22.9851L9.59125 14.4088L1.01495 13.7035C0.925 13.6961 0.831509 13.6884 0.751456 13.6755C0.661417 13.6609 0.550261 13.6344 0.438393 13.569C0.285653 13.4798 0.163171 13.3468 0.0867678 13.1873C0.0308103 13.0704 0.0135075 12.9575 0.00634064 12.8665C-2.96341e-05 12.7857 -1.4537e-05 12.6919 0 12.6017V12.6016V11.3984V11.3983C-1.4537e-05 11.3081 -2.96342e-05 11.2143 0.00634064 11.1335C0.0135075 11.0425 0.0308103 10.9296 0.0867678 10.8127C0.163171 10.6532 0.285653 10.5202 0.438393 10.431C0.55026 10.3656 0.661417 10.3391 0.751455 10.3246C0.831508 10.3116 0.924999 10.3039 1.01494 10.2965L9.59125 9.59125L10.2965 1.01495Z"
      fill="white"
      style={{ fill: "white", fillOpacity: 1 }}
    />
  </svg>
)

const ResumeSuraasaPlusTour = () => {
  const navigation = useNavigate()

  const {
    openOnboarding,
    setOpenOnboarding,
    spnyAiToolsTour,
    spnyCpatDashboardTour,
    spnyImpactCourseTour,
    spnyMasterclassTour,
    spnyJobsTour,
    isPendingTour,
    spnyPlatformIntro,
    isSpnyPlatformIntroCompleted,
  } = useSuraasaPlusNewYear()
  const { isSPNYUser } = useSuraasaPlusContext()
  const gradient = getTextGradientStyles(
    "linear-gradient(90deg, #49ACFF 0%, #E28FFF 69.77%)"
  )
  const gradientGold = getTextGradientStyles(
    "linear-gradient(90deg, #FFB400 0%, #FBDA00 69.77%)"
  )

  const toursProgress = useMemo(
    () => [
      {
        title: "Advanced AI Tools",
        description:
          "Save hours of planning and immediately take back your weekends.",
        route: routes.aiTools.home,
        isCompleted: spnyAiToolsTour === SpnyTourEnum.completed,
      },
      {
        title: "CPAT Skill Analysis",
        description:
          "Understand your strengths and plan your career growth strategically",
        route: routes.cpat.dashboard,
        isCompleted: spnyCpatDashboardTour === SpnyTourEnum.completed,
      },
      {
        title: "IMPACT Course",
        description:
          "Designed to build confidence and support measurable classroom impact.",
        route: routes.learning,
        isCompleted: spnyImpactCourseTour === SpnyTourEnum.completed,
      },
      {
        title: "Expert Masterclasses",
        description:
          "Explore 60+ expert masterclass recordings and tune in for the upcoming ones.",
        route: "masterclass",
        isCompleted: spnyMasterclassTour === SpnyTourEnum.completed,
      },
      {
        title: "Premium Jobs",
        description:
          "Discover and apply to unlimited premium teaching jobs at top schools around the globe.",
        route: getPlatformURL("jobs", "/"),
        isCompleted: spnyJobsTour === SpnyTourEnum.completed,
      },
    ],
    [
      spnyAiToolsTour,
      spnyCpatDashboardTour,
      spnyImpactCourseTour,
      spnyJobsTour,
      spnyMasterclassTour,
    ]
  )

  const leftTour = toursProgress.filter(item => !item.isCompleted)
  const isCompleted = spnyPlatformIntro === SpnyTourEnum.completed

  useEffect(() => {
    return () => {
      setOpenOnboarding(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!isSPNYUser) return null

  if (isCompleted && !isPendingTour && leftTour.length === 0) {
    return null
  }

  if (leftTour.length == 1) {
    const currentPending = leftTour[0]
    return (
      <div className="relative mb-2 overflow-hidden rounded-[21px] bg-[#261E12] p-2.5">
        <Typography className="relative z-2 mb-0.5 text-base font-[650] leading-[100%] tracking-[-4%] text-white">
          Explore your <span style={gradientGold}>{currentPending.title}</span>
        </Typography>
        <Typography
          variant="smallBody"
          className="relative z-2 mb-5 text-white"
        >
          {currentPending.description}
        </Typography>
        <div className="relative z-2 flex items-center justify-between gap-2">
          <Button
            onClick={() => {
              if (currentPending.route.includes("jobs")) {
                tourManager.completed(BROWSER_STORAGE_KEYS.spnyJobs)
                window.location.href = currentPending.route
              } else {
                navigation(currentPending.route)
              }
            }}
            color={null}
            className="bg-white text-onSurface-900"
          >
            Continue
          </Button>
          <PlusSvg />
        </div>
        <div
          className="absolute left-0 top-0 z-1 size-full"
          style={{
            backgroundImage: `radial-gradient(200px at 25% 185px, rgb(159, 104, 20) 0px, transparent 100%), radial-gradient(200px at 55% 250px, rgba(255, 151, 71, 0.75) 0px, transparent 100%)`,
          }}
        />
      </div>
    )
  }
  return (
    <>
      <div className="relative mb-2 overflow-hidden rounded-[21px] bg-[#0D1226] p-2.5">
        <Typography className="relative z-2 mb-0.5 text-base font-[650] leading-[100%] tracking-[-4%] text-white">
          Resume your <span style={gradient}>Suraasa Plus</span> tour
        </Typography>
        <Typography
          variant="smallBody"
          className="relative z-2 mb-5 text-white"
        >
          Jump back in and continue exploring what you’ve unlocked with Suraasa
          Plus.
        </Typography>
        <div className="relative z-2 flex items-center justify-between gap-2">
          <Button
            onClick={() => {
              setOpenOnboarding("dashboard-personal-onboarding")
            }}
            color={null}
            className="bg-white text-onSurface-900"
          >
            Resume Tour
          </Button>
          <PlusSvg />
        </div>
        <div
          className="absolute left-0 top-0 z-1 size-full"
          style={{
            backgroundImage: `radial-gradient(134px at 20% 200px,rgba(255, 71, 160, 0.7) 0,transparent 100%),radial-gradient(134px at 60% 200px,rgba(73, 20, 159, 1) 0,transparent 100%)`,
          }}
        />
      </div>
      <GetStarted
        open={openOnboarding === "dashboard-personal-onboarding"}
        handleClose={() => {
          setOpenOnboarding(null)
        }}
        isPendingTour={isPendingTour && isSpnyPlatformIntroCompleted}
      />
    </>
  )
}

export default ResumeSuraasaPlusTour
